import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMemberHistory, revertMember, clearMemberHistory } from '../../actions/memberHistory';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Paper,
    Chip,
    Box,
    Collapse,
    IconButton
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import RestoreIcon from '@material-ui/icons/Restore';
import moment from 'moment';

const HistoryRow = ({ entry, previousSnapshotData, onRevert, reverting, memberId }) => {
    const [expanded, setExpanded] = useState(false);
    const dispatch = useDispatch();

    const handleRevert = async () => {
        if (window.confirm('Are you sure you want to revert to this version? This will replace the current member data with the snapshot from ' + moment(entry.changed_at).format('MMM D, YYYY h:mm A'))) {
            try {
                const result = await dispatch(revertMember(entry._id, memberId));
                if (onRevert) onRevert(result.member);
                alert('Member reverted successfully!');
            } catch (err) {
                alert('Failed to revert member: ' + (err.response?.data?.error || err.message));
            }
        }
    };

    const getChangedByLabel = (changedBy) => {
        switch (changedBy) {
            case 'sync': return { label: 'Sync', color: 'primary' };
            case 'admin_update': return { label: 'Admin Update', color: 'secondary' };
            case 'revert': return { label: 'Revert', color: 'default' };
            case 'api': return { label: 'API', color: 'default' };
            default: return { label: changedBy, color: 'default' };
        }
    };

    const changedByInfo = getChangedByLabel(entry.changed_by);

    // Helper to compare two snapshots and return a set of changed keys
    const getChangedFields = (current, previous, fields) => {
        const changed = new Set();
        fields.forEach(({ key }) => {
            if ((current?.[key] || '') !== (previous?.[key] || '')) {
                changed.add(key);
            }
        });
        return changed;
    };

    // Get the key fields to display in the snapshot preview
    const snapshotPreviewFields = [
        { key: 'clinic_name', label: 'Clinic Name' },
        { key: 'street1', label: 'Street 1' },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'zip', label: 'Zip' },
        { key: 'phone', label: 'Phone' },
        { key: 'notification_emails', label: 'Notification Emails' },
        { key: 'appointment_type_name_new_patient', label: 'New Patient Appt' },
        { key: 'appointment_type_length_new_patient', label: 'New Patient Length' },
        { key: 'appointment_type_name_returning_patient', label: 'Returning Patient Appt' },
        { key: 'appointment_type_length_returning_patient', label: 'Returning Patient Length' }
    ];

    const currentSnapshot = entry.snapshot;
    const previousSnapshot = previousSnapshotData ?? entry.previous_snapshot;

    // Compute changed fields for highlighting
    const changedFields = getChangedFields(currentSnapshot, previousSnapshot, snapshotPreviewFields);

    return (
        <>
            <TableRow hover>
                <TableCell>
                    {moment(entry.changed_at).format('MMM D, YYYY h:mm A')}
                </TableCell>
                <TableCell>
                    <Chip 
                        label={changedByInfo.label} 
                        color={changedByInfo.color}
                        size="small"
                    />
                </TableCell>
                <TableCell>
                    {entry.changes && typeof entry.changes === 'object' && !entry.changes.reverted_to ? (
                        <Typography variant="body2">
                            {Object.keys(entry.changes).join(', ')}
                        </Typography>
                    ) : entry.changes?.reverted_to ? (
                        <Typography variant="body2" color="textSecondary">
                            Reverted from {moment(entry.changes.reverted_from_date).format('MMM D, YYYY')}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="textSecondary">—</Typography>
                    )}
                </TableCell>
                <TableCell>
                    <Box display="flex" alignItems="center">
                        <IconButton size="small" aria-label={expanded ? 'Collapse snapshot' : 'Expand snapshot'} onClick={() => setExpanded(!expanded)}>
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<RestoreIcon />}
                            onClick={handleRevert}
                            disabled={reverting}
                            style={{ marginLeft: 8 }}
                        >
                            Revert
                        </Button>
                    </Box>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box margin={2}>
                            <Typography variant="subtitle2" gutterBottom>
                                Snapshot Data
                            </Typography>
                            <Paper variant="outlined" style={{ padding: 16 }}>
                                <Table size="small">
                                    <TableBody>
                                        {snapshotPreviewFields.map(({ key, label }) => (
                                            <TableRow key={key} style={changedFields.has(key) ? { background: '#fffbe6' } : {}}>
                                                <TableCell component="th" scope="row" style={{ width: '40%' }}>
                                                    <strong>{label}</strong>
                                                </TableCell>
                                                <TableCell>
                                                    {currentSnapshot?.[key] || <em style={{ color: '#999' }}>empty</em>}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const MemberHistoryModal = ({ member, open, onClose, embedded = false, onRevert }) => {
    const dispatch = useDispatch();
    const { history, loading, reverting } = useSelector(state => state.memberHistory);

    useEffect(() => {
        if (open && member?._id) {
            dispatch(fetchMemberHistory(member._id));
        }
        return () => {
            if (!open) {
                dispatch(clearMemberHistory());
            }
        };
    }, [open, member?._id, dispatch]);

    const content = loading ? (
                    <Box display="flex" justifyContent="center" padding={4}>
                        <CircularProgress />
                    </Box>
                ) : history.length === 0 ? (
                    <Box display="flex" justifyContent="center" padding={4}>
                        <Typography color="textSecondary">
                            No history available for this member.
                        </Typography>
                    </Box>
                ) : (
                    <Table size="small" aria-label="Member history">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Changed By</TableCell>
                                <TableCell>Fields Changed</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.map((entry, idx) => {
                                // For the latest entry, compare to current member
                                let previousSnapshotData = idx === 0 ? member : history[idx - 1]?.snapshot;
                                return (
                                    <HistoryRow
                                        key={entry._id}
                                        entry={entry}
                                        previousSnapshotData={previousSnapshotData}
                                        reverting={reverting}
                                        memberId={member._id}
                                        onRevert={onRevert}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                );

    if (embedded) return <div className="member-history-panel">{content}</div>;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Member History - {member?.clinic_name || member?.clinic_id}
            </DialogTitle>
            <DialogContent dividers>
                {content}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MemberHistoryModal;
